import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Box,
  IconButton,
  Radio,
  Checkbox,
} from "@mui/material";
import { useSelector } from "react-redux";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const EXPAND_COLUMN_WIDTH = 28;

const withAlpha = (hex, alpha) => {
  if (!hex || typeof hex !== "string") return hex;
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return `${hex}${alpha}`;
  return `#${clean}${alpha}`;
};

const isParentRow = (row) => Number(row.itemparentid) === 0;

const getChildRows = (parent, allRows) =>
  allRows.filter((r) => Number(r.itemparentid) === Number(parent.siantySeId));

const getParentRows = (allRows) => allRows.filter(isParentRow);

const isRowApproved = (row) => Number(row.approved) === 1;

const isCustomerApproved = (row) => {
  const value = row?.customerApproved ?? row?.attributes?.customerApproved;
  return Number(value) === 1 || value === true;
};

const parentMatchesSearch = (parent, allRows, searchTerm) => {
  const term = searchTerm.toLowerCase();
  const parentName = `${parent.subTypeLabel || ""}`.toLowerCase();
  if (parentName.includes(term)) return true;
  return getChildRows(parent, allRows).some((c) =>
    `${c.subTypeLabel || ""}`.toLowerCase().includes(term),
  );
};

export default function TableCheckboxSelection({
  data: rows,
  decisions = {},
  onDecisionsChange,
  isViewChecbox,
  isSendToCust,
  onUpdatedRowsChange,
  maintenance,
  fullApproval = false,
  // Customer Approve: nothing is pre-decided or locked, every line item must be
  // explicitly marked Approve or Rejected.
  disableRowLocking = false,
  translate = (key) => key,
}) {
  const serverSettings = useSelector((state) => state.ServerSetting);
  const themecolors = useSelector((state) => state.themeColors) || {};
  const [search, setSearch] = useState("");
  const [rowMarkups, setRowMarkups] = useState({});
  const [rowUnitPrices, setRowUnitPrices] = useState({});
  const [sortField, setSortField] = useState("id");
  const [sortAsc, setSortAsc] = useState(true);
  const [expandedParentIds, setExpandedParentIds] = useState({});

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const themeDark = themecolors.themeDarkColor || "#161f35";
  const themeLight = themecolors.themeLightColor || "#00cbd7";
  const themeInverse = themecolors.themeInverse || "#ffffff";
  const themeListing = themecolors.themeListingColor || themeDark;
  const textColor = themecolors.textColor || themeDark;
  const bgColor = themecolors.backgroundColor || "#ffffff";
  const successColor = themecolors.success || "#4BB543";
  const errorColor = themecolors.error || "#ff424c";
  const borderColor = withAlpha(themeListing, "4D");
  const headerBg = withAlpha(themeListing, "1A");
  const mutedText = withAlpha(textColor, "99");
  const selectedBg = withAlpha(successColor, "1A");
  const selectedHoverBg = withAlpha(successColor, "33");
  const rowHover = withAlpha(themeLight, "14");
  const childBg = withAlpha(themeListing, "0F");

  const approveControlSx = {
    color: successColor,
    "&.Mui-checked": { color: successColor },
    "&.MuiCheckbox-indeterminate": { color: successColor },
  };

  const rejectControlSx = {
    color: errorColor,
    "&.Mui-checked": { color: errorColor },
    "&.MuiCheckbox-indeterminate": { color: errorColor },
  };

  const headCellSx = {
    whiteSpace: "nowrap",
    backgroundColor: headerBg,
    color: textColor,
    fontWeight: 600,
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    py: 1.25,
    px: 1.25,
    borderBottom: `1px solid ${borderColor}`,
    lineHeight: 1.3,
    textAlign: "left",
  };

  const bodyCellSx = {
    py: 1,
    px: 1.25,
    fontSize: "0.8125rem",
    color: textColor,
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: "nowrap",
    textAlign: "left",
  };

  const valueCellSx = {
    ...bodyCellSx,
    fontVariantNumeric: "tabular-nums",
  };

  const decisionHeadCellSx = {
    ...headCellSx,
    textAlign: "center",
    verticalAlign: "middle",
    px: 1,
    minWidth: 88,
    width: 88,
  };

  const decisionBodyCellSx = {
    ...bodyCellSx,
    textAlign: "center",
    px: 1,
    minWidth: 88,
    width: 88,
  };

  const compactInputSx = {
    "& .MuiOutlinedInput-root": {
      height: 30,
      fontSize: "0.78rem",
      backgroundColor: bgColor,
      borderRadius: "6px",
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: themeLight },
      "&.Mui-focused fieldset": { borderColor: themeLight, borderWidth: 1 },
    },
    "& .MuiInputBase-input": {
      py: 0.5,
      px: 1,
      textAlign: "left",
      fontVariantNumeric: "tabular-nums",
      color: textColor,
    },
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        WebkitAppearance: "none",
        margin: 0,
      },
  };

  const isLockedParent = (row) =>
    disableRowLocking ? false : isRowApproved(row) || isCustomerApproved(row);

  const getPurchasePrice = (row) => {
    if (row?.attributes?.theirPrice) {
      return Number(row.attributes.theirPrice);
    }
    return Number(row?.unitPrice);
  };

  const calcUnitPriceFromMarkup = (purchasePrice, markUpValue) =>
    purchasePrice + (purchasePrice * markUpValue) / 100;

  const calcMarkupFromUnitPrice = (purchasePrice, unitPrice) => {
    if (!purchasePrice || Number.isNaN(purchasePrice)) return 0;
    return ((unitPrice - purchasePrice) / purchasePrice) * 100;
  };

  useEffect(() => {
    setRowMarkups((prevMarkups) => {
      const nextMarkups = { ...prevMarkups };
      rows.forEach((row) => {
        if (
          nextMarkups[row.id] === undefined ||
          nextMarkups[row.id] === null ||
          nextMarkups[row.id] === ""
        ) {
          nextMarkups[row.id] =
            row?.markUpValue ??
            row?.markup ??
            row?.attributes?.markUpValue ??
            row?.attributes?.markup ??
            "";
        }
      });
      return nextMarkups;
    });

    setRowUnitPrices((prevUnitPrices) => {
      const nextUnitPrices = { ...prevUnitPrices };
      rows.forEach((row) => {
        if (
          nextUnitPrices[row.id] === undefined ||
          nextUnitPrices[row.id] === null ||
          nextUnitPrices[row.id] === ""
        ) {
          const purchasePrice = getPurchasePrice(row);
          const markUpValue = Number(
            row?.markUpValue ??
              row?.markup ??
              row?.attributes?.markUpValue ??
              row?.attributes?.markup ??
              0,
          );
          if (!Number.isNaN(purchasePrice)) {
            nextUnitPrices[row.id] = calcUnitPriceFromMarkup(
              purchasePrice,
              markUpValue,
            );
          } else {
            nextUnitPrices[row.id] = "";
          }
        }
      });
      return nextUnitPrices;
    });
  }, [rows]);

  useEffect(() => {
    if (!isViewChecbox || !rows?.length || !onDecisionsChange) return;
    if (disableRowLocking) return;

    const lockedParents = getParentRows(rows).filter(isLockedParent);
    if (!lockedParents.length) return;

    onDecisionsChange((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      lockedParents.forEach((parent) => {
        if (next[parent.id] !== "approve") {
          next[parent.id] = "approve";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [rows, isViewChecbox, onDecisionsChange, disableRowLocking]);

  const setDecision = (parentId, value) => {
    if (typeof onDecisionsChange !== "function") return;
    onDecisionsChange((prev) => ({
      ...(prev || {}),
      [parentId]: value,
    }));
  };

  const handleSelectAllApprove = () => {
    if (typeof onDecisionsChange !== "function") return;
    const next = { ...(decisions || {}) };
    getParentRows(rows).forEach((parent) => {
      next[parent.id] = "approve";
    });
    onDecisionsChange(next);
  };

  const handleSelectAllReject = () => {
    if (typeof onDecisionsChange !== "function") return;
    const next = { ...(decisions || {}) };
    getParentRows(rows).forEach((parent) => {
      if (!isLockedParent(parent)) {
        next[parent.id] = "reject";
      }
    });
    onDecisionsChange(next);
  };

  const handleToggleAllApprove = (checked) => {
    if (typeof onDecisionsChange !== "function") return;
    if (checked) {
      handleSelectAllApprove();
      return;
    }
    const next = { ...(decisions || {}) };
    getParentRows(rows).forEach((parent) => {
      if (!isLockedParent(parent)) {
        delete next[parent.id];
      }
    });
    onDecisionsChange(next);
  };

  const handleToggleAllReject = (checked) => {
    if (typeof onDecisionsChange !== "function") return;
    if (checked) {
      handleSelectAllReject();
      return;
    }
    const next = { ...(decisions || {}) };
    getParentRows(rows).forEach((parent) => {
      if (!isLockedParent(parent) && next[parent.id] === "reject") {
        delete next[parent.id];
      }
    });
    onDecisionsChange(next);
  };

  const toggleParentExpanded = (parentId) => {
    setExpandedParentIds((prev) => ({
      ...prev,
      [parentId]: !prev[parentId],
    }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleMarkupChange = (rowId, value, purchasePrice) => {
    const markUpValue = Number(value) || 0;
    setRowMarkups((prev) => ({
      ...prev,
      [rowId]: markUpValue,
    }));
    if (!Number.isNaN(purchasePrice) && purchasePrice != null) {
      setRowUnitPrices((prev) => ({
        ...prev,
        [rowId]: calcUnitPriceFromMarkup(purchasePrice, markUpValue),
      }));
    }
  };

  const handleUnitPriceChange = (rowId, value, purchasePrice) => {
    const unitPrice = Number(value) || 0;
    setRowUnitPrices((prev) => ({
      ...prev,
      [rowId]: unitPrice,
    }));
    if (!Number.isNaN(purchasePrice) && purchasePrice != null) {
      setRowMarkups((prev) => ({
        ...prev,
        [rowId]: calcMarkupFromUnitPrice(purchasePrice, unitPrice),
      }));
    }
  };

  const getRowPricing = (row) => {
    const quantity = Number(row?.quantity) || 0;
    const markUpValue = Number(rowMarkups[row.id]) || 0;
    const vatPercentage = Number(serverSettings?.vatPercentage) || 0;

    if (isSendToCust) {
      const purchasePrice = getPurchasePrice(row);

      if (Number.isNaN(purchasePrice)) {
        return {
          purchasePrice: null,
          markUpValue,
          unitPrice: null,
          cost: null,
          saleNetAmount: null,
          saleVat: null,
        };
      }

      const unitPrice =
        rowUnitPrices[row.id] !== undefined &&
        rowUnitPrices[row.id] !== null &&
        rowUnitPrices[row.id] !== ""
          ? Number(rowUnitPrices[row.id]) || 0
          : calcUnitPriceFromMarkup(purchasePrice, markUpValue);
      const cost = unitPrice * quantity;
      const saleVat = cost * (vatPercentage / 100);
      const saleNetAmount = saleVat + cost;
      const updatedMarketPrice = !row?.marketPrice ? unitPrice : row.marketPrice;

      return {
        purchasePrice,
        markUpValue,
        unitPrice,
        cost,
        saleNetAmount,
        saleVat,
        updatedMarketPrice,
      };
    }

    // Approve / other modes: show stored sale values (read-only)
    const unitPrice = Number(row?.unitPrice);
    const cost =
      row?.cost != null && row?.cost !== ""
        ? Number(row.cost)
        : Number.isNaN(unitPrice)
          ? null
          : unitPrice * quantity;
    const saleVat =
      row?.saleVATAmount != null && row?.saleVATAmount !== ""
        ? Number(row.saleVATAmount)
        : cost != null && !Number.isNaN(cost)
          ? cost * (vatPercentage / 100)
          : null;
    const saleNetAmount =
      row?.saleNetAmount != null && row?.saleNetAmount !== ""
        ? Number(row.saleNetAmount)
        : cost != null && saleVat != null
          ? cost + saleVat
          : null;

    return {
      purchasePrice: null,
      markUpValue,
      unitPrice: Number.isNaN(unitPrice) ? null : unitPrice,
      cost: cost != null && !Number.isNaN(cost) ? cost : null,
      saleNetAmount:
        saleNetAmount != null && !Number.isNaN(saleNetAmount)
          ? saleNetAmount
          : null,
      saleVat: saleVat != null && !Number.isNaN(saleVat) ? saleVat : null,
      updatedMarketPrice: row?.marketPrice,
    };
  };

  useEffect(() => {
    if (!isSendToCust || !onUpdatedRowsChange) return;

    const mappedRows = rows.map((row) => {
      const pricing = getRowPricing(row);
      return {
        ...row,
        markUpValue: pricing.markUpValue,
        markup: pricing.markUpValue,
        cost: pricing.cost,
        unitPrice: pricing.unitPrice,
        saleNetAmount: pricing.saleNetAmount,
        saleVATAmount: pricing.saleVat,
        marketPrice: pricing.updatedMarketPrice,
        attributes: {
          ...(row.attributes || {}),
          markup: pricing.markUpValue,
        },
      };
    });

    onUpdatedRowsChange(mappedRows);
  }, [rows, rowMarkups, rowUnitPrices, isSendToCust, onUpdatedRowsChange]);

  const parentRows = getParentRows(rows);

  const filteredParentRows = parentRows.filter((parent) =>
    parentMatchesSearch(parent, rows, search),
  );

  const filteredSortedParentRows = [...filteredParentRows].sort((a, b) => {
    const valA = a[sortField];
    const valB = b[sortField];
    if (valA === null || valA === undefined) return 1;
    if (valB === null || valB === undefined) return -1;
    if (valA < valB) return sortAsc ? -1 : 1;
    if (valA > valB) return sortAsc ? 1 : -1;
    return 0;
  });

  const paginatedParentRows = filteredSortedParentRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const selectableParents = parentRows.filter(
    (parent) => !isLockedParent(parent),
  );

  const allApproved =
    parentRows.length > 0 &&
    parentRows.every((parent) => decisions?.[parent.id] === "approve");

  const someApproved =
    parentRows.some((parent) => decisions?.[parent.id] === "approve") &&
    !allApproved;

  const allRejected =
    selectableParents.length > 0 &&
    selectableParents.every((parent) => decisions?.[parent.id] === "reject");

  const someRejected =
    selectableParents.some((parent) => decisions?.[parent.id] === "reject") &&
    !allRejected;

  // When the approve/reject radios are not shown (isViewChecbox false), we fall
  // back to a single read-only column that just reflects the approved status.
  const showReadOnlyApproved = !isViewChecbox && !maintenance;

  const renderHeaderDecisionCell = (type) => {
    const isApprove = type === "approve";
    const checked = isApprove ? allApproved : allRejected;
    const indeterminate = isApprove ? someApproved : someRejected;
    const onToggle = isApprove ? handleToggleAllApprove : handleToggleAllReject;

    return (
      <TableCell align="center" sx={decisionHeadCellSx}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={0.5}
        >
          <Box
            component="span"
            sx={{
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: "0.03em",
              textTransform: "uppercase",
              color: isApprove ? successColor : errorColor,
              whiteSpace: "nowrap",
            }}
          >
            {translate(isApprove ? "approve" : "rejected")}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={0}
          >
            <Checkbox
              size="small"
              checked={checked}
              indeterminate={indeterminate}
              disabled={fullApproval}
              onChange={(e) => onToggle(e.target.checked)}
              sx={{
                p: 0.25,
                ...(isApprove ? approveControlSx : rejectControlSx),
              }}
            />
            <Box
              component="span"
              sx={{
                fontSize: "0.62rem",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                color: mutedText,
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 500,
              }}
            >
              {translate("selectAll")}
            </Box>
          </Box>
        </Box>
      </TableCell>
    );
  };

  const renderHeaderSelectionCell = () => {
    if (isViewChecbox) {
      return (
        <>
          {renderHeaderDecisionCell("approve")}
          {!fullApproval && renderHeaderDecisionCell("reject")}
        </>
      );
    }
    if (showReadOnlyApproved) {
      return <TableCell align="center" sx={decisionHeadCellSx} />;
    }
    return null;
  };

  const renderDecisionCells = (row, isChild = false) => {
    if (isViewChecbox) {
      if (isChild) {
        return (
          <>
            <TableCell align="center" sx={decisionBodyCellSx} />
            {!fullApproval && (
              <TableCell align="center" sx={decisionBodyCellSx} />
            )}
          </>
        );
      }

      const locked = isLockedParent(row);
      // In full-approval mode every parent is approved and inputs are locked.
      const decision = fullApproval ? "approve" : decisions?.[row.id] || "";

      return (
        <>
          <TableCell align="center" sx={decisionBodyCellSx}>
            <Radio
              size="small"
              checked={decision === "approve"}
              disabled={locked || fullApproval}
              onChange={() => setDecision(row.id, "approve")}
              value="approve"
              name={`decision-${row.id}`}
              sx={approveControlSx}
            />
          </TableCell>
          {!fullApproval && (
            <TableCell align="center" sx={decisionBodyCellSx}>
              <Radio
                size="small"
                checked={decision === "reject"}
                disabled={locked || fullApproval}
                onChange={() => setDecision(row.id, "reject")}
                value="reject"
                name={`decision-${row.id}`}
                sx={rejectControlSx}
              />
            </TableCell>
          )}
        </>
      );
    }

    if (showReadOnlyApproved) {
      const approved = isRowApproved(row);
      return (
        <TableCell align="center" sx={decisionBodyCellSx}>
          <Checkbox
            checked={approved}
            disabled
            icon={
              <DisabledByDefaultIcon
                style={{ color: approved ? successColor : errorColor }}
              />
            }
            checkedIcon={
              <CheckBoxIcon style={{ color: successColor }} />
            }
          />
        </TableCell>
      );
    }

    return null;
  };

  const renderDataRow = (row, options = {}) => {
    const {
      isChild = false,
      rowIndex,
      showExpand = false,
      isExpanded = false,
      childCount = 0,
      onToggleExpand,
    } = options;

    const pricing = getRowPricing(row);
    const decision = decisions?.[row.id];
    const selected = isViewChecbox
      ? fullApproval || decision === "approve"
      : isRowApproved(row);

    return (
      <TableRow
        key={row.id}
        selected={selected}
        hover
        sx={{
          backgroundColor: isChild
            ? childBg
            : selected
              ? selectedBg
              : "transparent",
          transition: "background-color 0.15s ease",
          "&.Mui-selected": { backgroundColor: selectedBg },
          "&.Mui-selected:hover": { backgroundColor: selectedHoverBg },
          "&:hover": {
            backgroundColor: selected ? selectedHoverBg : rowHover,
          },
          "& td": { borderBottom: `1px solid ${borderColor}` },
        }}
      >
        {renderDecisionCells(row, isChild)}
        <TableCell sx={{ ...bodyCellSx, color: mutedText, width: 56 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            {!isChild && (
              <Box
                sx={{
                  width: EXPAND_COLUMN_WIDTH,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {showExpand && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleExpand();
                    }}
                    aria-label={isExpanded ? "Collapse" : "Expand"}
                    sx={{
                      p: 0.25,
                      color: mutedText,
                      "&:hover": {
                        backgroundColor: withAlpha(themeLight, "22"),
                      },
                    }}
                  >
                    {isExpanded ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowRightIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
            )}
            <span>{rowIndex}</span>
          </Box>
        </TableCell>
        <TableCell
          sx={{
            ...bodyCellSx,
            pl: isChild ? 3.5 : 1.25,
            fontWeight: isChild ? 400 : 600,
          }}
        >
          <Box component="span">
            {row.subTypeLabel}
            {!isChild && childCount > 0 && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  color: mutedText,
                  fontSize: "0.72rem",
                  fontWeight: 500,
                }}
              >
                ({childCount})
              </Box>
            )}
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 0.85,
              py: 0.2,
              borderRadius: "4px",
              backgroundColor: withAlpha(themeListing, "1A"),
              color: textColor,
              fontSize: "0.72rem",
              fontWeight: 500,
            }}
          >
            {row.expenseType || "-"}
          </Box>
        </TableCell>
        {isSendToCust && (
          <TableCell sx={valueCellSx}>
            {pricing.purchasePrice != null
              ? Number(pricing.purchasePrice).toFixed(2)
              : "-"}
          </TableCell>
        )}
        <TableCell sx={{ ...bodyCellSx, py: 0.6 }}>
          {isSendToCust && row?.attributes?.found === "false" ? (
            <TextField
              size="small"
              type="number"
              placeholder="0.00"
              value={
                rowUnitPrices[row.id] !== undefined &&
                rowUnitPrices[row.id] !== null
                  ? rowUnitPrices[row.id]
                  : (pricing.unitPrice ?? "")
              }
              onChange={(e) =>
                handleUnitPriceChange(
                  row.id,
                  e.target.value,
                  pricing.purchasePrice,
                )
              }
              inputProps={{ min: 0, step: "0.01" }}
              sx={{ width: 88, ...compactInputSx }}
            />
          ) : (
            <Box component="span" sx={valueCellSx}>
              {pricing.unitPrice != null
                ? Number(pricing.unitPrice).toFixed(2)
                : "-"}
            </Box>
          )}
        </TableCell>
        {isSendToCust && (
          <TableCell sx={{ ...bodyCellSx, py: 0.6 }}>
            {row?.attributes?.found === "false" ? (
              <TextField
                size="small"
                type="number"
                placeholder="0"
                value={rowMarkups[row.id] ?? ""}
                onChange={(e) =>
                  handleMarkupChange(
                    row.id,
                    e.target.value,
                    pricing.purchasePrice,
                  )
                }
                inputProps={{ min: 0, step: "0.01" }}
                sx={{ width: 68, ...compactInputSx }}
              />
            ) : (
              <Box component="span" sx={{ ...valueCellSx, color: mutedText }}>
                -
              </Box>
            )}
          </TableCell>
        )}
        <TableCell sx={valueCellSx}>{row.quantity ?? "-"}</TableCell>
        <TableCell sx={{ ...valueCellSx, color: mutedText }}>
          {pricing.cost != null ? Number(pricing.cost).toFixed(2) : "-"}
        </TableCell>
        <TableCell sx={{ ...valueCellSx, fontWeight: 600 }}>
          {pricing.saleNetAmount != null
            ? Number(pricing.saleNetAmount).toFixed(2)
            : "-"}
        </TableCell>
      </TableRow>
    );
  };

  const decisionColumnCount = isViewChecbox
    ? fullApproval
      ? 1
      : 2
    : showReadOnlyApproved
      ? 1
      : 0;

  const totals = rows.reduce(
    (acc, row) => {
      const pricing = getRowPricing(row);
      acc.withoutVat += Number(pricing.cost) || 0;
      acc.withVat += Number(pricing.saleNetAmount) || 0;
      return acc;
    },
    { withoutVat: 0, withVat: 0 },
  );

  // isSendToCust: decisions + # + Name + Type + Purchase + Sale Price + Markup + Qty
  // else: decisions + # + Name + Type + Sale Price + Qty
  const totalLabelColSpan = decisionColumnCount + (isSendToCust ? 7 : 5);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        border: `1px solid ${borderColor}`,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: bgColor,
        color: textColor,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${borderColor}`,
          backgroundColor: bgColor,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{
            width: 260,
            "& .MuiOutlinedInput-root": {
              height: 34,
              fontSize: "0.8125rem",
              borderRadius: "8px",
              backgroundColor: headerBg,
              color: textColor,
              "& fieldset": { borderColor },
              "&:hover fieldset": { borderColor: themeLight },
              "&.Mui-focused fieldset": {
                borderColor: themeLight,
                borderWidth: 1,
              },
            },
            "& .MuiInputBase-input": { color: textColor },
          }}
        />
        <Box
          sx={{
            fontSize: "0.75rem",
            color: mutedText,
            fontWeight: 500,
          }}
        >
          {filteredSortedParentRows.length} item
          {filteredSortedParentRows.length === 1 ? "" : "s"}
        </Box>
      </Box>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {renderHeaderSelectionCell()}
              <TableCell
                onClick={() => handleSort("subTypeLabel")}
                sx={{ ...headCellSx, cursor: "pointer" }}
              >
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Box
                    sx={{
                      width: EXPAND_COLUMN_WIDTH,
                      flexShrink: 0,
                    }}
                  />
                  <span>#</span>
                </Box>
              </TableCell>
              <TableCell
                onClick={() => handleSort("subTypeLabel")}
                sx={{ ...headCellSx, cursor: "pointer", pl: 1.25 }}
              >
                Name
              </TableCell>
              <TableCell
                onClick={() => handleSort("expenseType")}
                sx={{ ...headCellSx, cursor: "pointer" }}
              >
                Type
              </TableCell>
              {isSendToCust && (
                <TableCell
                  onClick={() => handleSort("attributes.theirPrice")}
                  sx={{ ...headCellSx, cursor: "pointer" }}
                >
                  Purchase Price
                </TableCell>
              )}
              <TableCell
                onClick={() => handleSort("unitPrice")}
                sx={{ ...headCellSx, cursor: "pointer" }}
              >
                Sale Price
              </TableCell>
              {isSendToCust && (
                <TableCell sx={headCellSx}>Markup (%)</TableCell>
              )}
              <TableCell
                onClick={() => handleSort("quantity")}
                sx={{ ...headCellSx, cursor: "pointer" }}
              >
                Quantity
              </TableCell>
              <TableCell sx={headCellSx}>Sale (W/O VAT)</TableCell>
              <TableCell
                onClick={() => handleSort("saleNetAmount")}
                sx={{ ...headCellSx, cursor: "pointer" }}
              >
                Sale (W/ VAT)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedParentRows.map((parent, index) => {
              const children = getChildRows(parent, rows);
              const isExpanded = !!expandedParentIds[parent.id];
              const rowIndex = page * rowsPerPage + index + 1;

              return (
                <React.Fragment key={parent.id}>
                  {renderDataRow(parent, {
                    rowIndex,
                    showExpand: children.length > 0,
                    isExpanded,
                    childCount: children.length,
                    onToggleExpand: () => toggleParentExpanded(parent.id),
                  })}
                  {isExpanded &&
                    children.map((child, childIndex) =>
                      renderDataRow(child, {
                        isChild: true,
                        rowIndex: `${rowIndex}.${childIndex + 1}`,
                      }),
                    )}
                </React.Fragment>
              );
            })}
            <TableRow
              sx={{
                backgroundColor: themeDark,
                "& td": {
                  color: themeInverse,
                  fontWeight: 600,
                  borderBottom: "none",
                  py: 1.25,
                  px: 1.25,
                  fontSize: "0.8125rem",
                  textAlign: "left",
                },
              }}
            >
              <TableCell
                colSpan={totalLabelColSpan}
                align="left"
                sx={{
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  fontSize: "0.7rem !important",
                  color: `${withAlpha(themeInverse, "B3")} !important`,
                }}
              >
                {translate("total")}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {totals.withoutVat.toFixed(2)}
              </TableCell>
              <TableCell
                align="left"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {totals.withVat.toFixed(2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredSortedParentRows.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25]}
        sx={{
          borderTop: `1px solid ${borderColor}`,
          color: mutedText,
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
            {
              fontSize: "0.75rem",
              color: mutedText,
            },
          "& .MuiTablePagination-select": {
            fontSize: "0.75rem",
          },
        }}
      />
    </Paper>
  );
}
